// shared/components/ui/FreshnessIndicator.tsx
// ────────────────────────────────────────────────────────────────────────────
// Data-freshness badge (FE/UIX directive: mandatory data-freshness badges —
// live/cached/stale/offline). A tiny pure component consumed by any feature
// hook that exposes React Query state.
//
// State machine:
//   • offline  → no connectivity (navigator.onLine === false)
//   • live     → isFetching || lastFetchedAt within `liveWindowMs`
//   • stale    → query is marked stale OR lastFetchedAt > staleWindowMs
//   • cached   → otherwise (fresh, idle)
//
// Pure visual, no side-effects. Tokens consumed from PAC Glass palette.
// ────────────────────────────────────────────────────────────────────────────
import React from 'react';

export type FreshnessState = 'live' | 'cached' | 'stale' | 'offline';

export interface FreshnessIndicatorProps {
  /** Explicitly override the computed freshness state. */
  state?:         FreshnessState;
  /** ms since epoch of last successful fetch (e.g. query.dataUpdatedAt). */
  lastFetchedAt?: number | null;
  /** True while a fetch is in flight. */
  isFetching?:    boolean;
  /** True if React Query marks the cache as stale. */
  isStale?:       boolean;
  /** True if the last fetch failed or we are offline. */
  isError?:       boolean;
  /** Anything fresher than this is "live" (default: 5s). */
  liveWindowMs?:  number;
  /** Anything older than this is "stale" (default: 60s). */
  staleWindowMs?: number;
  /** Compact dot-only mode (no label). */
  compact?:       boolean;
  /** Optional override of the displayed label. */
  label?:         string;
  className?:     string;
}

const STATE_STYLES: Record<FreshnessState, { dot: string; text: string; label: string; pulse: boolean }> = {
  live:    { dot: 'bg-emerald-400', text: 'text-emerald-300/90',  label: 'live',    pulse: true  },
  cached:  { dot: 'bg-sky-400',     text: 'text-sky-300/90',      label: 'cached',  pulse: false },
  stale:   { dot: 'bg-amber-400',   text: 'text-amber-300/90',    label: 'stale',   pulse: false },
  offline: { dot: 'bg-rose-500',    text: 'text-rose-300/90',     label: 'offline', pulse: false },
};

export function computeFreshness(p: {
  lastFetchedAt?: number | null;
  isFetching?:    boolean;
  isStale?:       boolean;
  isError?:       boolean;
  liveWindowMs?:  number;
  staleWindowMs?: number;
  isOnline?:      boolean;
}): FreshnessState {
  const {
    lastFetchedAt, isFetching, isStale, isError,
    liveWindowMs = 5_000, staleWindowMs = 60_000,
    isOnline = typeof navigator === 'undefined' ? true : navigator.onLine,
  } = p;
  if (!isOnline || isError) return 'offline';
  if (isFetching) return 'live';
  if (!lastFetchedAt) return isStale ? 'stale' : 'cached';
  const age = Date.now() - lastFetchedAt;
  if (age <= liveWindowMs) return 'live';
  if (isStale || age >= staleWindowMs) return 'stale';
  return 'cached';
}

export const FreshnessIndicator: React.FC<FreshnessIndicatorProps> = ({
  state,
  lastFetchedAt, isFetching, isStale, isError,
  liveWindowMs, staleWindowMs,
  compact = false, label, className = '',
}) => {
  const [, force] = React.useReducer((x: number) => x + 1, 0);

  // Re-render once per 10s so age-derived state advances without polling
  // the server. Cheap and avoids "stuck live" badges on idle screens.
  React.useEffect(() => {
    const id = window.setInterval(force, 10_000);
    return () => window.clearInterval(id);
  }, []);

  // React to online/offline transitions.
  React.useEffect(() => {
    const on  = () => force();
    window.addEventListener('online',  on);
    window.addEventListener('offline', on);
    return () => {
      window.removeEventListener('online',  on);
      window.removeEventListener('offline', on);
    };
  }, []);

  const computedState = state ?? computeFreshness({ lastFetchedAt, isFetching, isStale, isError, liveWindowMs, staleWindowMs });
  const s     = STATE_STYLES[computedState];
  const text  = label ?? s.label;
  const title = lastFetchedAt
    ? `${text} · updated ${new Date(lastFetchedAt).toLocaleTimeString()}`
    : text;

  return (
    <span
      role="status"
      aria-live="polite"
      aria-label={`data ${computedState}`}
      title={title}
      data-testid="freshness-indicator"
      data-state={computedState}
      className={`inline-flex items-center gap-1.5 text-[10px] font-medium tracking-wide uppercase ${s.text} ${className}`}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot} ${s.pulse ? 'animate-pulse' : ''}`} />
      {!compact && <span>{text}</span>}
    </span>
  );
};

export default FreshnessIndicator;
